import { handlePayment, handlePaymentStarted, listenToRetailerBasket, pushProductsToBasket, resetOnEmptyBasket } from "./basket-sync";
import { getAbCssUrl, getPlannerEntryAbVariants } from "./config-file";
import { getPosExtId } from "./utils";


const MEALZ_LOG_LEVEL = 'none';
// const MEALZ_LOG_LEVEL = 'all'; // Uncomment to activate logs
const AB_STORAGE_KEY = '_mealz/injector/ab';
const PLANNER_ENTRY_AB_STORAGE_KEY = '_mealz/injector/ab/planner-entry';


/* **************************************** CALLBACKS **************************************** */

function hookCallback(isLogged, isPosValid) {
  if (!isLogged) {
    document.querySelector("[data-login]").click();
    return false
  }
  if (!isPosValid) {
    if (getPosExtId()?.length === 0) {
      document.querySelector(".change-store-new").click()
    }
    return false;
  }
  return true;
}

/**
 * Queries CoursesU API to change store, and reload page when response received
 */
async function forcePosCallback(posExtId) {
  const url = "https://www.coursesu.com/on/demandware.store/Sites-DigitalU-Site/fr_FR/StoreLocator-SetStore?format=ajax";
  const csrfToken = document.getElementsByName("csrf_token")[0].value;
  const encodedCsrfToken = encodeURI(csrfToken);
  const fullUrl = `${url}&store_id=${posExtId}&csrf_token=${encodedCsrfToken}`;

  try {
    const response = await fetch(fullUrl, {
      credentials: "include",
      headers: {
        "User-Agent": "Mozilla/5.0 (X11; Ubuntu; Linux x86_64; rv:124.0) Gecko/20100101 Firefox/124.0",
        "Accept": "*/*",
        "Accept-Language": "fr,fr-FR;q=0.8,en-US;q=0.5,en;q=0.3",
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Sec-Fetch-Dest": "empty",
        "Sec-Fetch-Mode": "no-cors",
        "Sec-Fetch-Site": "same-origin",
        "Pragma": "no-cache",
        "Cache-Control": "no-cache",
      },
      referrer: "https://www.coursesu.com/recherche-tout-en-un",
      method: "GET",
      mode: "cors",
    });

    if (response.ok) {
      globalThis.location.reload();
    } else {
      console.error("Failed to set store. Status:", response.status);
    }
  } catch (error) {
    console.error("An error occurred while setting the store:", error);
  }
}


/* **************************************** BASKET SYNC **************************************** */

function initMealzBasketSync() {
  const MEALZ = globalThis.mealz;
  MEALZ.basketSync.definePushProductsToCart(pushProductsToBasket)
  handlePayment();
  listenToRetailerBasket();
  resetOnEmptyBasket();
  handlePaymentStarted();
}


/* **************************************** OPTIONALS **************************************** */

function initMealzOptionals() {
  const MEALZ = globalThis.mealz;
  MEALZ.router.setRecipeCatalogUrl('/idees-repas.html'); // TODO: remove when V3 deprecate it
  MEALZ.router.setRetailerCartUrl('/panier'); // TODO: remove when V3 deprecate it
}


/* **************************************** A/B TESTING **************************************** */

async function initAbTesting() {
  const MEALZ = globalThis.mealz;
  // If an a/b testing campaign is on (defined in config file)
  const abConfig = await getAbCssUrl();
  if (abConfig) {
    console.debug('[Mealz Injector] A/B testing: config found', abConfig);
    // Define use as "a", "b", "c"... randomly
    // If user is already assigned a letter, let as is
    if (!localStorage.getItem(AB_STORAGE_KEY)) {
      const max = Object.keys(abConfig).length
      const rand = Math.floor(Math.random() * max);
      const version = Object.keys(abConfig)[rand];
      localStorage.setItem(AB_STORAGE_KEY, version);
    }

    // Configure analytics service to attach the letter to each event
    const abTestKey = localStorage.getItem(AB_STORAGE_KEY);
    console.debug('[Mealz Injector] A/B testing: User set to version', abTestKey);
    MEALZ.analytics.setAbTestKey(abTestKey);
    // Load the proper stylesheet using url in config file
    injectCss(abConfig[abTestKey]);

  // Otherwise, remove any previous a/b testing config kept in storage
  } else {
    console.debug('[Mealz Injector] A/B testing: no config found');
    localStorage.removeItem(AB_STORAGE_KEY);
  }

  const plannerEntryAbVariants = await getPlannerEntryAbVariants();
  const plannerEntryVariants = normalizeAbVariants(plannerEntryAbVariants);
  if (plannerEntryVariants.length === 0) {
    localStorage.removeItem(PLANNER_ENTRY_AB_STORAGE_KEY);
    return;
  }

  const currentVariant = localStorage.getItem(PLANNER_ENTRY_AB_STORAGE_KEY);
  if (currentVariant && plannerEntryVariants.includes(currentVariant)) {
    return;
  }

  const assignedVariant = plannerEntryVariants[Math.floor(Math.random() * plannerEntryVariants.length)];
  localStorage.setItem(PLANNER_ENTRY_AB_STORAGE_KEY, assignedVariant);
}

function normalizeAbVariants(variantsConfig) {
  if (Array.isArray(variantsConfig)) {
    return variantsConfig
      .map(variant => String(variant).trim())
      .filter(Boolean);
  }

  if (variantsConfig && typeof variantsConfig === 'object') {
    return Object.keys(variantsConfig)
      .map(variant => String(variant).trim())
      .filter(Boolean);
  }

  return [];
}

function injectCss(url) {
  // in config file, set "a": null to keep default version of stylesheet
  if (!url) {
    console.debug('[Mealz Injector] A/B testing: loading no css file');
    return;
  }

  console.debug('[Mealz Injector] A/B testing: loading css file', url);
  const head = document.head;
  const link = document.createElement("link");

  link.type = "text/css";
  link.rel = "stylesheet";
  link.href = url;

  head.appendChild(link);
}


/* **************************************** INIT MEALZ **************************************** */

export async function initMealz() {
  const MEALZ = globalThis.mealz;
  MEALZ.config.mealzLogs(MEALZ_LOG_LEVEL)
  try {
    await initAbTesting();
  } catch (error) {
    console.warn(
      "[Mealz Injector] A/B testing initialization failed, continuing without it:",
      error
    );
  }
  MEALZ.hook.setHookCallback((isLogged, isPosValid) => hookCallback(isLogged, isPosValid));
  MEALZ.hook.setForcePosCallback((posExtId) => forcePosCallback(posExtId));
  initMealzBasketSync();
  initMealzOptionals();
}
